import NameLinkCell from '@/components/NameLinkCell';
import { sampleQueryKeys } from '@/config/queryKeys';
import { defaultColDef } from '@/constants';
import { useDateFormatter, useRouteProtection } from '@/hooks';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import { type PaginatedSamplesResponse, SUPER_ADMIN_ROLE, type SampleWithNamesPublic } from '@/types';
import { applyStoredColumnVisibility } from '@/utils/applyStoredColumnVisibility';
import { flushColumnState, sizeColumnsOnFirstVisit } from '@/utils/flushColumnState';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import type { ColDef, GridApi, ICellRendererParams } from 'ag-grid-enterprise';
import type { AgGridReact } from 'ag-grid-react';
import React, { type FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import globalStyles from '../../styles/index.module.css';

import InstrumentRunCellRenderer from './components/InstrumentRunCellRenderer';
import StudyCellRenderer from './components/StudyCellRenderer';
import { PAGE_SIZE } from './constants/PAGE_SIZE';
import { useOrgId } from './helpers';
import styles from './styles.module.css';

export const SamplesPage: FC = () => {
  // Protect this route - will redirect if user loses SuperAdmin role or switches org
  useRouteProtection([SUPER_ADMIN_ROLE]);
  const { t } = useTranslation(['studies', 'general']);
  const orgId = useOrgId();
  const gridRef = useRef<AgGridReact<SampleWithNamesPublic> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const formatDate = useDateFormatter();

  const colDefs = useMemo<ColDef<SampleWithNamesPublic>[]>(
    () => [
      { field: 'sample_group', headerName: t('columnHeaders.sampleGroup'), hide: true },
      {
        field: 'name',
        headerName: t('columnHeaders.sampleName'),
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) =>
          params.data ? (
            <NameLinkCell
              ariaLabel={t('goToSample')}
              className={styles.nameCell}
              fullHeight
              id={params.data.id}
              name={params.data.name}
              params={{ sampleId: params.data.id }}
              to="/samples/$sampleId"
            />
          ) : null,
        minWidth: 200,
        width: 300,
      },
      {
        field: 'study_name',
        headerName: t('columnHeaders.studyName'),
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => (
          <StudyCellRenderer className={styles.nameCell} data={params.data} fullHeight />
        ),
        width: 300,
      },
      {
        field: 'instrument_run_name',
        headerName: t('columnHeaders.runName'),
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => (
          <InstrumentRunCellRenderer className={styles.nameCell} data={params.data} fullHeight />
        ),
        minWidth: 160,
      },
      { field: 'status', headerName: t('columnHeaders.status') },
      { field: 'run_qc', headerName: t('columnHeaders.runQc') },
      { field: 'image_qc', headerName: t('columnHeaders.imageQc') },
      { field: 'decode_qc', headerName: t('columnHeaders.decodeQc') },
      { field: 'assay_version', headerName: t('columnHeaders.assay'), hide: true },
      { field: 'species', headerName: t('columnHeaders.species'), hide: true },
      {
        field: 'created_at',
        headerName: t('columnHeaders.created'),
        hide: true,
        valueFormatter: ({ value }: { value: string }) => formatDate(value),
      },
      {
        field: 'updated_at',
        headerName: t('columnHeaders.updated'),
        hide: true,
        valueFormatter: ({ value }: { value: string | null | undefined }) => (value ? formatDate(value) : ''),
      },
      {
        // TODO: expose `created_by` on SampleWithNamesPublic when backend supports it
        colId: 'created_by',
        headerName: t('columnHeaders.createdBy'),
        hide: true,
        valueGetter: () => undefined,
      },
      {
        // TODO: expose `updated_by` on SampleWithNamesPublic when backend supports it
        colId: 'updated_by',
        headerName: t('columnHeaders.updatedBy'),
        hide: true,
        valueGetter: () => undefined,
      },
    ],
    [formatDate, t],
  );

  const datasource = useMemo(() => {
    return orgId
      ? createAgGridDatasource<SampleWithNamesPublic, PaginatedSamplesResponse>(
          sampleQueryKeys.endpoints.listSearch(orgId),
        )
      : undefined;
  }, [orgId]);

  const handleGridReady = (params: { api: GridApi<SampleWithNamesPublic> }) => {
    // Restore column visibility before the design system's own persistence runs.
    // When "Size Columns to Fit Width" clears the full column state the design
    // system only re-applies hide:true for stored hidden columns, never hide:false
    // for columns the user made visible. This call fixes that gap.
    applyStoredColumnVisibility(params.api, 'samples-table');
    if (datasource) {
      params.api.setGridOption('serverSideDatasource', datasource);
    }
    sizeColumnsOnFirstVisit(params.api, 'samples-table');
  };

  return (
    <Stack className={styles.container} fullHeight fullWidth orientation="vertical" gap={4}>
      <Text as="h1" size="xl">
        {t('title')}
      </Text>

      <Table
        container={{ className: globalStyles.scrollbars, ref: containerRef }}
        columnDefs={colDefs}
        defaultColDef={defaultColDef}
        density="normal"
        domLayout="normal"
        enableResponsiveResize={false}
        getRowId={(params: { data: SampleWithNamesPublic }) => params.data.id}
        onGridReady={handleGridReady}
        pagination
        paginationPageSize={PAGE_SIZE}
        ref={gridRef}
        resizeDebounceMs={150}
        rowBuffer={30}
        rowModelType="serverSide"
        tableId="samples-table"
        onGridPreDestroyed={e => flushColumnState(e.api, 'samples-table')}
        zebraStriping
      />
    </Stack>
  );
};

export default SamplesPage;
